import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import type { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import {
  useLookupEmployees,
  normalizeLookupAlias,
  normalizeLookupAliases,
} from "components/user-search-input/endpoints";
import { GaiaConstants } from "utils/gaia-constants";

export interface UseEnrichedMemberOptions {
  options: SelectProps.Option[];
  isEnriching: boolean;
}

// Enriches a static team-member option list with directory display info:
// each option keeps its value/label and gains a "Full Name · Title" description.
// Options already carrying a description, or with no resolvable alias, pass
// through unchanged.
export const useEnrichedMemberOptions = (
  baseOptions: SelectProps.Option[],
  teamName: string | undefined,
  enabled = true,
): UseEnrichedMemberOptions => {
  const aliases = useMemo(
    () =>
      normalizeLookupAliases(
        baseOptions
          .filter((o) => o.value && !o.description)
          .map((o) => o.value),
      ),
    [baseOptions],
  );

  const skip =
    !enabled ||
    !teamName ||
    teamName === GaiaConstants.PLACE_HOLDER ||
    aliases.length === 0;

  const arg = skip ? undefined : { teamName, aliases };

  const { data, isLoading } = useLookupEmployees(arg ?? skipToken);

  const options = useMemo(() => {
    if (!data) return baseOptions;
    return baseOptions.map((option) => {
      if (option.description || !option.value) return option;
      const key = normalizeLookupAlias(option.value);
      if (!key) return option;
      const detail = data.employees[key];
      if (!detail) return option;
      const description =
        detail.fullName && detail.businessTitle
          ? `${detail.fullName} · ${detail.businessTitle}`
          : detail.fullName || undefined;
      return description ? { ...option, description } : option;
    });
  }, [baseOptions, data]);

  return { options, isEnriching: isLoading };
};
